import { useContext } from 'react';

import { RenderingContext } from './rendering-context';

/**
 * Read one presentation setting — a value that controls *how* a block looks rather than what it says
 * (`objectFit`, `backgroundColor`, `height`, …) — without caring where the provider keeps it.
 *
 * The two CMSs disagree on that, and the disagreement is structural rather than incidental:
 *
 * - **Sitecore** models these as *rendering parameters*: per-placement values from the rendering's
 *   Parameters Template, which the layout service returns in `params`, separate from field content.
 * - **Sanity** has no equivalent concept, so the CMS schema compiles each parameter into an
 *   ordinary field on the block (grouped `appearance`), which reaches the component as `content`.
 *
 * A component reading only one of the two renders unstyled on the other provider — exactly the
 * failure the `?_cms=sitecore` / `?_cms=sanity` comparison is meant to catch, so it is fixed here once
 * instead of per component. `params` wins when both carry the key: on Sitecore it is the authoritative
 * per-placement override, and on Sanity it is never populated.
 */
export const useSetting = (name: string): string | undefined => {
  const rendering = useContext(RenderingContext);
  const fromParams = rendering?.params?.[name];
  if (fromParams !== undefined && fromParams !== null && fromParams !== '') return String(fromParams);

  const fromContent = (rendering?.content as Record<string, unknown> | undefined)?.[name];
  if (typeof fromContent === 'string') return fromContent === '' ? undefined : fromContent;
  if (typeof fromContent === 'number' || typeof fromContent === 'boolean') return String(fromContent);
  return undefined;
};
